import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Stack,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import { useState, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { formatearFechaLocal, normalizarFechaISO } from '../../utils/horaLocal';
import { 
  ModalMovimientos, 
  ModalEditarMovimiento, 
  ModalSeleccionarTipoSalida 
} from './ModalesInventario';
import InventarioFiltersBar from './components/InventarioFiltersBar';
import { filterMovimientos } from './utils/inventarioFilters';

export default function HistorialMovimientos({
  movimientos,
  productos,
  anularMovimiento,
  state,
  filters,
  onFiltersChange,
}) {
  const [open, setOpen] = useState(false);
  const [openTipoSalida, setOpenTipoSalida] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [tipoMov, setTipoMov] = useState('ENTRADA');
  const [subTipo, setSubTipo] = useState(null);
  const [movimientoEditando, setMovimientoEditando] = useState(null);

  const díaBloqueado = state.verificarDíaBloqueado();
  const arqueoDelDía = state.obtenerArqueoDelDía();

  const handleOpen = (tipo) => {
    if (tipo === 'SALIDA') {
      setOpenTipoSalida(true);
    } else {
      setTipoMov(tipo);
      setSubTipo(null);
      setOpen(true);
    }
  };

  const handleSeleccionarTipoSalida = (tipo) => {
    setTipoMov('SALIDA');
    setSubTipo(tipo);
    setOpenTipoSalida(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSubTipo(null);
  };

  const abrirEditar = (movimiento) => {
    setMovimientoEditando(movimiento);
    setOpenEditar(true);
  };

  const handleCloseEditar = () => {
    setOpenEditar(false);
    setMovimientoEditando(null);
  };

  const lista = useMemo(() => {
    return [...filterMovimientos(movimientos, filters)].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );
  }, [movimientos, filters]);

  return (
    <>
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Kardex (Precio unitario promedio)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registro multiproducto · {lista.length} movimiento(s) en el periodo
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Button 
              fullWidth 
              variant="contained" 
              color="success" 
              onClick={() => handleOpen('ENTRADA')}
              disabled={díaBloqueado}
            >
              + Compra Material
            </Button>
            <Button 
              fullWidth 
              variant="contained" 
              color="error" 
              onClick={() => handleOpen('SALIDA')}
              disabled={díaBloqueado}
            >
              - Salida o Venta
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const n = prompt('Nombre del producto:');
                if (n) state.agregarProducto(n);
              }}
              disabled={díaBloqueado}
            >
              Nuevo Producto
            </Button>
          </Stack>
        </Stack>

        {díaBloqueado && (
          <Box sx={{ 
            bgcolor: '#ffebee', 
            p: 2, 
            borderRadius: 2, 
            border: '2px solid #f44336',
            mb: 3
          }}>
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '1.1rem' }}>
                🔒 DÍA BLOQUEADO - ARQUEO DE CAJA COMPLETADO
              </Typography>
              <Typography variant="body2" sx={{ color: '#c62828' }}>
                No puedes agregar nuevos movimientos. El arqueo de caja fue completado hoy a las {new Date(arqueoDelDía.fecha).toLocaleTimeString('es-BO')}.
              </Typography>
              <Typography variant="body2" sx={{ color: '#c62828', fontStyle: 'italic' }}>
                Para desbloquear, ve a la pestaña "Arqueo de Caja" y anula el arqueo de hoy.
              </Typography>
            </Stack>
          </Box>
        )}

        <InventarioFiltersBar
          filters={filters}
          onChange={onFiltersChange}
          productos={productos}
          movimientos={movimientos}
          exportDataset="kardex"
          title="Filtros"
        />

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Transacción</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">P. Unit.</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Método</TableCell>
                <TableCell align="center">Acción</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {lista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay movimientos con los filtros seleccionados.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lista.map(m => (
                  <TableRow key={m.id} sx={{ opacity: m.anulado ? 0.4 : 1 }}>
                    <TableCell>{formatearFechaLocal(normalizarFechaISO(m.fecha))}</TableCell>
                    <TableCell>
                      <strong>{(m.transactionID || m.id).substring(0, 8).toUpperCase()}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{productos.find(p => p.id === m.productoId)?.nombre}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          m.subTipo === 'LIMPIEZA_ORIGEN' ? 'Limpieza (origen)'
                            : m.subTipo === 'LIMPIEZA_RESULTADO' ? 'Limpieza (compra)'
                            : m.subTipo && m.tipo !== 'ENTRADA' ? m.subTipo : m.tipo
                        }
                        size="small"
                        color={m.tipo === 'ENTRADA' ? 'success' : m.subTipo === 'VENTA' ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">{m.cantidad}</TableCell>
                    <TableCell align="right">{(m.precio || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <strong>{((m.cantidad || 0) * (m.precio || 0)).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={m.metodoPago || 'EFECTIVO'} 
                        size="small"
                        color={m.metodoPago === 'QR' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!m.anulado && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton 
                            size="small" 
                            color="primary"
                            disabled={díaBloqueado}
                            onClick={() => abrirEditar(m)}
                            title="Editar"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            color="error"
                            disabled={díaBloqueado}
                            onClick={() => anularMovimiento(m.id)}
                          >
                            Anular
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <ModalMovimientos
        open={open}
        onClose={handleClose}
        tipo={tipoMov}
        subTipo={subTipo}
        state={state}
        productos={productos}
        díaBloqueado={díaBloqueado}
      />

      <ModalEditarMovimiento
        open={openEditar}
        onClose={handleCloseEditar}
        movimiento={movimientoEditando}
        state={state}
        productos={productos}
        díaBloqueado={díaBloqueado}
      />

      <ModalSeleccionarTipoSalida
        open={openTipoSalida}
        onClose={() => setOpenTipoSalida(false)}
        onSeleccionar={handleSeleccionarTipoSalida}
      />
    </>
  );
}
